/**
 * プラン変更（05-ui.md §8.9）。
 *
 * ダウングレードで上限割れが起きる場合は **ボタンを押させない**。
 * 押してから「12 件削除してください」と言われるのは体験として最悪なので、
 * 現在の使用量とプラン上限を突き合わせて、事前に理由を出す。
 *
 * **適用時期（`applyAt`）を必ず送る。** 以前はどの変更も既定の
 * `period_end` で飛んでいたため、**上位プランへ上げても次回更新日まで
 * 何も起きなかった**（容量が足りなくて上げた契約者が、翌月まで
 * 足りないまま待たされる）。ここで
 *   - アップグレード（値上がり）… `now`（即時）
 *   - ダウングレード（値下がり）… `period_end`（期末）
 * を決めて送り、確認文言も同じ判定で出し分ける。
 * **サーバ側でも値下がりは期末に倒される**ので、画面が誤っても
 * 「突然容量が縮む」事故にはならない（判定の正はサーバ）。
 *
 * 年払いもここで選べる。LP が「年払いは 2 か月分お得」と掲示しているのに、
 * 申し込む経路が製品のどこにも無かった。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  formatCount,
} from '@mail-sv/ui';
import { ArrowLeft, Check, Minus } from 'lucide-react';
import { DEFAULT_TAX_RATE_BP, formatJpyWithTax, taxIncludedAmount } from '@mail-sv/shared';
import { api, publicApi } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useOrg } from '../lib/hooks.js';
import { AppLink } from '../lib/nav.js';
import { Async, ErrorAlert } from '../components/async.js';
import { ConfirmDialogButton } from '../components/confirm-button.js';
import { JobProgressDialog } from '../components/job-progress.js';
import { formatLimit, formatQuota } from '../lib/format.js';
import { useAuth, canManageBilling } from '../stores/auth.js';
import type { CountedList, JobAccepted, PublicPlan, Subscription } from '../lib/api-types.js';

/** 表示する金額（税抜）。周期の切り替えで参照先が変わる。 */
export function planPrice(plan: PublicPlan, period: 'monthly' | 'yearly'): number {
  return period === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
}

/**
 * 送る適用時期。**値上がり（同額を含む）は即時、値下がりだけ期末。**
 *
 * 以前はどの変更も既定の `period_end` で飛んでいたため、上位プランへ
 * 上げても次回更新日まで容量が増えなかった。逆にここを `'now'` へ倒し切ると
 * 「下位プランへ変えた瞬間に容量が縮む」ので、**同額は即時・値下がりは期末**
 * の境界そのものが仕様。判定の正はサーバだが、確認文言と送る値は
 * この 1 か所で揃える。
 */
export function planChangeApplyAt(
  price: number,
  currentPeriodPrice: number,
): 'now' | 'period_end' {
  return price >= currentPeriodPrice ? 'now' : 'period_end';
}

export interface Blocker {
  label: string;
  current: number;
  max: number;
}

export function BillingPlanPage() {
  const { user } = useAuth();
  const org = useOrg();
  const queryClient = useQueryClient();
  const [jobId, setJobId] = React.useState<string | null>(null);

  const plans = useQuery({
    queryKey: qk.plans(),
    // `/public/plans` は配列ではなく `{items,total}` を返す。
    queryFn: () => publicApi.get<CountedList<PublicPlan>>('/plans'),
    staleTime: 5 * 60_000,
  });

  /** 現在の契約（お支払い周期と現行価格の判定に使う）。 */
  const subscription = useQuery({
    queryKey: qk.subscription(),
    queryFn: () => api.get<Subscription>('/billing/subscription'),
    staleTime: 60_000,
  });

  const [period, setPeriod] = React.useState<'monthly' | 'yearly'>('monthly');
  // 契約中の周期を初期選択にする（年払いの契約者に月額表を見せない）。
  React.useEffect(() => {
    if (subscription.data) setPeriod(subscription.data.billingPeriod);
  }, [subscription.data]);

  const change = useMutation({
    mutationFn: (input: { planCode: string; applyAt: 'now' | 'period_end' }) =>
      api.post<JobAccepted>('/billing/plan-change', {
        planCode: input.planCode,
        billingPeriod: period,
        applyAt: input.applyAt,
      }),
    onSuccess: (res) => setJobId(res.jobId),
  });

  const canManage = canManageBilling(user);
  const currentCode = org.data?.plan.code;
  const currentPrice = subscription.data?.price ?? 0;

  return (
    <>
      <PageHeader
        title="プランの変更"
        description="ご利用状況に合わせてプランを変更できます。上位プランへの変更はすぐに、下位プランへの変更は次回更新日に適用されます。"
        breadcrumbs={[{ label: '料金プラン・請求', href: '/billing' }, { label: 'プラン変更' }]}
        actions={
          <Button asChild variant="outline" size="sm">
            <AppLink to="/billing">
              <ArrowLeft aria-hidden />
              戻る
            </AppLink>
          </Button>
        }
      />

      <div className="space-y-5">
        {!canManage ? (
          <Alert variant="warning">
            <AlertTitle>プランを変更する権限がありません</AlertTitle>
            <AlertDescription>
              プランの変更は契約者（オーナー）のみ行えます。内容の確認のみ可能です。
            </AlertDescription>
          </Alert>
        ) : null}

        {change.isError ? <ErrorAlert error={change.error} /> : null}

        {/*
         * お支払い周期の切り替え。**年払いを選ぶ経路がここにしか無い。**
         * LP は「年払いは 2 か月分お得」と掲示しているのに、申し込む手段が
         * 製品のどこにも無かった。プラン変更 API は `billingPeriod` を
         * 受け取るので、同じ操作で周期も切り替える。
         */}
        <div
          className="inline-flex rounded-lg border border-border bg-panel p-1"
          role="group"
          aria-label="お支払い周期"
        >
          {(['monthly', 'yearly'] as const).map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={period === p}
              onClick={() => setPeriod(p)}
              className={
                period === p
                  ? 'rounded-md bg-background px-3 py-1.5 text-sm font-medium text-foreground shadow-sm'
                  : 'rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground'
              }
            >
              {p === 'monthly' ? '月払い' : '年払い（2 か月分お得）'}
            </button>
          ))}
        </div>

        <Async query={plans} lines={5}>
          {(list) => {
            /*
             * 現行プランの価格。アップグレード / ダウングレードの判定に使う。
             * **判定の正はサーバ**（値下がりは `applyAt` に関わらず期末に倒す）。
             * ここでの判定は「確認文言と送る `applyAt` を合わせる」ためのもの。
             */
            const currentPlan = list.items.find((p) => p.code === currentCode);
            const currentPeriodPrice = currentPlan ? planPrice(currentPlan, period) : currentPrice;

            return (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {list.items.map((plan) => {
                // 現行プランの判定は `/portal/org` の plan.code と突き合わせる
                // （公開プラン一覧は無認証なので契約情報を持たない）。
                const isCurrent = plan.code === currentCode;
                const blockers = org.data ? findBlockers(plan, org.data.usage) : [];
                const price = planPrice(plan, period);
                // 同額は「上げも下げもしない」ので即時で問題ない。
                const applyAt = planChangeApplyAt(price, currentPeriodPrice);
                const upgrade = applyAt === 'now';
                return (
                  <Card
                    key={plan.code}
                    className={isCurrent ? 'border-primary shadow-primary/20' : undefined}
                  >
                    <CardHeader className="pb-3">
                      <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
                        {plan.name}
                        {isCurrent ? (
                          <Badge variant="default" size="sm">
                            現在のプラン
                          </Badge>
                        ) : null}
                      </CardTitle>
                      {/*
                       * **総額表示**（消費税法 63 条）。税込を主に出す。
                       * 変換は `@mail-sv/shared` の純関数を通す（画面で
                       * 1.1 を掛けない。税率が変わったとき必ず取り残される）。
                       */}
                      <p className="text-2xl font-semibold">
                        {price > 0 ? formatJpyWithTax(price, DEFAULT_TAX_RATE_BP) : '個別見積り'}
                        {price > 0 ? (
                          <span className="ml-1 text-sm font-normal text-muted-foreground">
                            / {period === 'yearly' ? '年' : '月'}
                          </span>
                        ) : null}
                      </p>
                      {period === 'yearly' && plan.monthlyPrice > 0 && plan.yearlyPrice > 0 ? (
                        /*
                         * **換算値も税込で出す。** 主表示（上）が税込なのに
                         * ここだけ税抜だと、同じカードに 2 つの物差しが並ぶ。
                         * 総額表示（消費税法 63 条）の趣旨は「支払う額が
                         * ひと目で分かること」なので、併記の税抜は括弧に落とす。
                         * 換算は**税抜のまま割ってから**税込にする
                         * （税込を割ると端数が税率ごとに 1 回の丸めから外れる）。
                         */
                        <p className="text-xs text-muted-foreground">
                          月払いに換算すると{' '}
                          {formatCount(
                            taxIncludedAmount(
                              Math.round(plan.yearlyPrice / 12),
                              DEFAULT_TAX_RATE_BP,
                            ),
                          )}{' '}
                          円 / 月（税込・税抜 {formatCount(Math.round(plan.yearlyPrice / 12))} 円）
                        </p>
                      ) : null}
                      <p className="text-sm text-muted-foreground">{plan.description}</p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <dl className="space-y-1.5 text-sm">
                        <Row label="ドメイン" value={formatLimit(plan.limits.domainsMax, ' 個')} />
                        <Row label="メールアドレス" value={formatLimit(plan.limits.mailboxesMax, ' 件')} />
                        <Row label="エイリアス" value={formatLimit(plan.limits.aliasesMax, ' 件')} />
                        <Row label="合計容量" value={formatQuota(plan.limits.storageBytesTotal)} />
                        <Row
                          label="1 アドレスの容量上限"
                          value={formatQuota(plan.limits.mailboxQuotaBytesMax)}
                        />
                        <Row label="1 日の送信上限" value={formatLimit(plan.limits.sendPerDayMax, ' 通')} />
                        {/*
                          **`formatLimit()` を使わない。** この上限だけは
                          `0 = 無制限` ではなく `0 = この機能を使えない` なので、
                          そのまま渡すとライトプランに「無制限」と出る。
                        */}
                        <Row
                          label="プライベートアドレス"
                          value={
                            plan.limits.privateAddressesMax > 0
                              ? `${formatCount(plan.limits.privateAddressesMax)} 件`
                              : '—'
                          }
                        />
                      </dl>

                      <ul className="space-y-1 text-sm">
                        <Feature on={plan.features.webmail}>Webメール</Feature>
                        <Feature on={plan.features.autoresponder}>自動応答</Feature>
                        <Feature on={plan.features.catchAll}>キャッチオール</Feature>
                        <Feature on={plan.features.antivirus}>ウイルス検査</Feature>
                        <Feature on={plan.features.sieveManage}>振り分けルールの編集</Feature>
                        <Feature on={plan.features.apiAccess}>API 利用</Feature>
                        <Feature on={plan.features.prioritySupport}>優先サポート</Feature>
                        {/*
                          **上位プランで売っている機能は比較表に必ず並べる。**
                          画像プロキシと AI は設定画面を作ったのにここに無く、
                          「上位プランを買っても差が分からない」状態だった。
                        */}
                        <Feature on={plan.features.imageProxy}>外部画像の代理取得</Feature>
                        <Feature on={plan.features.imageProxyIpRotation}>
                          取得元 IP の入れ替え
                        </Feature>
                        <Feature on={plan.features.imageProxyPrefetch}>
                          受信時の先読み（開封時刻を隠す）
                        </Feature>
                        <Feature on={plan.features.aiAssistant}>
                          Webメールの AI（要約・返信文の提案）
                        </Feature>
                        <Feature on={plan.features.aiCustomEndpoint}>
                          AI の接続先を自社で指定
                        </Feature>
                      </ul>

                      {blockers.length > 0 && !isCurrent ? (
                        <Alert variant="warning">
                          <AlertTitle>このプランには変更できません</AlertTitle>
                          <AlertDescription>
                            <ul className="list-disc space-y-1 pl-4">
                              {blockers.map((b) => (
                                <li key={b.label}>
                                  現在 {formatCount(b.current)} の{b.label}がありますが、
                                  このプランの上限は {formatCount(b.max)} です。先に{' '}
                                  {formatCount(b.current - b.max)} 減らしてください。
                                </li>
                              ))}
                            </ul>
                          </AlertDescription>
                        </Alert>
                      ) : null}

                      {isCurrent ? (
                        <Button variant="outline" className="w-full" disabled>
                          ご利用中
                        </Button>
                      ) : (
                        <ConfirmDialogButton
                          variant="default"
                          size="default"
                          destructive={false}
                          title={`${plan.name} に変更します`}
                          description={
                            price <= 0
                              ? 'このプランは個別見積りです。担当者よりご連絡いたします。'
                              : upgrade
                                ? `変更後の${period === 'yearly' ? '年額' : '月額'}は ${formatJpyWithTax(
                                    price,
                                    DEFAULT_TAX_RATE_BP,
                                  )} です。すぐに適用され、差額は次回の請求に反映されます。`
                                : `変更後の${period === 'yearly' ? '年額' : '月額'}は ${formatJpyWithTax(
                                    price,
                                    DEFAULT_TAX_RATE_BP,
                                  )} です。下位プランへの変更は、容量や送信上限が突然縮まないよう次回更新日に適用します。`
                          }
                          confirmLabel="このプランに変更する"
                          pending={change.isPending}
                          disabled={!canManage || blockers.length > 0}
                          onConfirm={() => change.mutate({ planCode: plan.code, applyAt })}
                        >
                          このプランに変更
                        </ConfirmDialogButton>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            );
          }}
        </Async>
      </div>

      <JobProgressDialog
        open={jobId !== null}
        onOpenChange={(open) => {
          if (!open) setJobId(null);
        }}
        title="プランを変更しています"
        jobId={jobId}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: qk.org() });
          void queryClient.invalidateQueries({ queryKey: qk.subscription() });
        }}
      />
    </>
  );
}

/** ダウングレードで上限割れになる項目。0 は無制限なので対象外。 */
export function findBlockers(
  plan: PublicPlan,
  usage: { domains: number; mailboxes: number; aliases: number; storageBytes: number },
): Blocker[] {
  const blockers: Blocker[] = [];
  const check = (label: string, current: number, max: number) => {
    if (max > 0 && current > max) blockers.push({ label, current, max });
  };
  check('ドメイン', usage.domains, plan.limits.domainsMax);
  check('メールアドレス', usage.mailboxes, plan.limits.mailboxesMax);
  check('エイリアス', usage.aliases, plan.limits.aliasesMax);
  // 容量は「件数」ではないので、バイトのまま比較して GB 表現で見せる。
  if (plan.limits.storageBytesTotal > 0 && usage.storageBytes > plan.limits.storageBytesTotal) {
    blockers.push({
      label: '使用容量（GB）',
      current: Math.ceil(usage.storageBytes / 1024 ** 3),
      max: Math.floor(plan.limits.storageBytesTotal / 1024 ** 3),
    });
  }
  return blockers;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular font-medium">{value}</dd>
    </div>
  );
}

function Feature({ on, children }: { on: boolean; children: React.ReactNode }) {
  return (
    <li className={`flex items-center gap-2 ${on ? 'text-foreground' : 'text-muted-foreground'}`}>
      {on ? (
        <Check className="size-4 shrink-0 text-success" aria-hidden />
      ) : (
        <Minus className="size-4 shrink-0" aria-hidden />
      )}
      {children}
    </li>
  );
}
